import { ImageProps, VideoPlayerProps } from '@sato/components';
import { Asset, Entry as ContentfulEntry } from 'contentful';

import { Intersection, RequiredFields } from '../../models/utility.types';

import {
  CONTENT_TYPE,
  CONTENTFUL_DEFAULT_LOCALE_CODE,
  IEntry,
  IImage,
  IVideo,
  LOCALE_CODE,
} from './generated.types';

type ParsedField<T> = T extends Asset[]
  ? ParseAsset[]
  : T extends Asset
    ? ParseAsset
    : T extends EntryType[]
      ? ParsedEntry<T[number]>[]
      : T extends EntryType
        ? ParsedEntry<T>
        : T;

export type ParsedEntry<T extends IEntry> = {
  [K in keyof T['fields']]: ParsedField<NonNullable<T['fields'][K]>>;
} & {
  id: string;
  contentType: ContentType<T>;
};

export type ContentType<T extends EntryType> =
  T['sys']['contentType']['sys']['id'];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericEntry<T = any> = ContentfulEntry<T>;

export const isEntryType = (value: unknown): value is EntryType =>
  typeof value === 'object' &&
  !!value &&
  'fields' in value &&
  value.fields !== undefined;

export type EntryType = IEntry;

export type ContentfulAsset = Asset & { fields: { file: { upload?: string } } };

export type MediaEntry = ParsedEntry<IVideo | IImage>;
export type MediaComponentProps = Partial<
  Intersection<VideoPlayerProps, ImageProps>
>;

export type ParseAsset = {
  title: ContentfulAsset['fields']['title'];
  src: NonNullable<ContentfulAsset['fields']['file']['url' | 'upload']>;
  srcType: 'image' | 'video';
  alt: ContentfulAsset['fields']['description'];
  source: 'contentful';
};

export type LocalizedNonNullableField<T extends NonNullable<unknown>> =
  RequiredFields<
    Partial<
      Record<
        LOCALE_CODE,
        T extends GenericEntry | Asset
          ? LocalizedEntry<T>
          : T extends GenericEntry[] | Asset[]
            ? LocalizedEntry<T[number]>[]
            : T
      >
    >,
    CONTENTFUL_DEFAULT_LOCALE_CODE
  >;

// default locale is required but all locales are optional
export type LocalizedField<T> =
  T extends NonNullable<unknown>
    ? LocalizedNonNullableField<T>
    : LocalizedNonNullableField<NonNullable<T>> | undefined;

// localize all fields in object. By default localize also all sub fields recursively
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LocalizedFields<T extends object> = {
  [key in keyof T]: LocalizedField<T[key]>;
};

export type LocalizedEntry<T extends Asset | GenericEntry> = {
  fields: LocalizedFields<T['fields']>;
  update: () => Promise<LocalizedEntry<T>>;
} & Omit<T, 'fields' | 'update'>;

export type LocalizedEntryFields<T extends GenericEntry> =
  LocalizedEntry<T>['fields'];

/** Localize nested Entry and Assets fields and leave other fields unchanged */
export type ParsedLocalizedFields<
  T extends GenericEntry | Asset,
  F = T['fields'],
> = {
  [key in keyof F]: F[key] extends GenericEntry | Asset // `Asset` -> `LocalizedAsset<Asset>`
    ? LocalizedEntry<F[key]>
    : F[key] extends GenericEntry | Asset | undefined // `Asset | undefined` -> `LocalizedAsset<Asset> | undefined`
      ? LocalizedEntry<NonNullable<F[key]>> | undefined
      : F[key] extends Array<GenericEntry | Asset> // `Asset[]` -> `LocalizedAsset<Asset[]>`
        ? LocalizedEntry<F[key][number]>[]
        : F[key] extends Array<GenericEntry | Asset> | undefined // `Asset[] | undefined` -> `LocalizedAsset<Asset[]> | undefined`
          ? LocalizedEntry<NonNullable<F[key]>[number]>[] | undefined
          : F[key]; // otherwise, keep as-is
};

/** Similar to ParsedLocalizedFields but recursively removes all localization from all nested fields and
 * parses child entries/assets (with ParsedNestedLocalizedEntry), i.e. resolves field types from localized
 * maps and removes localization from all nested entry fields */
export type ParsedNestedLocalizedFields<
  T extends GenericEntry | Asset,
  F = T['fields'],
> = {
  [key in keyof F]: F[key] extends GenericEntry | Asset // `Asset` -> `ParsedAsset<Asset>`
    ? ParsedNestedLocalizedEntry<F[key]>
    : F[key] extends GenericEntry | Asset | undefined // `Asset | undefined` -> `ParsedAsset<Asset> | undefined`
      ? ParsedNestedLocalizedEntry<NonNullable<F[key]>> | undefined
      : F[key] extends Array<GenericEntry | Asset> // `Asset[]` -> `ParsedAsset<Asset[]>`
        ? ParsedNestedLocalizedEntry<F[key][number]>[]
        : F[key] extends Array<GenericEntry | Asset> | undefined // `Asset[] | undefined` -> `ParsedAsset<Asset[]> | undefined`
          ?
              | ParsedNestedLocalizedEntry<NonNullable<F[key]>[number]>[]
              | undefined
          : F[key]; // otherwise, keep as-is
};

export type ParsedLocalizedEntry<T extends GenericEntry | Asset> =
  ParsedLocalizedFields<T> & {
    type: CONTENT_TYPE | 'asset';
    id: string;
  };

export type ParsedNestedLocalizedEntry<T extends GenericEntry | Asset> =
  ParsedNestedLocalizedFields<T> & {
    type: CONTENT_TYPE | 'asset';
    id: string;
  };
